/**
 * weaponVisualConfig.ts — Per-weapon SOCKETS + feel data (BETA 22.69, Weapon Feel Beta A).
 *
 * Dylan's core rule: "The weapon sprite should visually aim at the enemy, and
 * the projectile should come out of the weapon tip." Every weapon defines its
 * pivot + muzzle sockets as PNG FRACTIONS of its sprite (the same measured
 * pattern as REVOLVER_MOD_MUZZLE), plus per-weapon aim/recoil/bob feel.
 *
 * LEAF MODULE — imports nothing from weaponSystem/weapons (no circular deps).
 *
 * Zero-regression contract: a weapon with NO entry here resolves to DEFAULTS,
 * which reproduce the pre-22.69 behavior exactly (legacy barrelLength path,
 * aim lerp dt*18, no recoil, no bob, generic muzzle flash).
 */

export type RestAngleMode = 'outward' | 'tangent' | 'fixed';
export type MuzzleFlashType = 'generic' | 'none';
export type ProjectileSpawnMode = 'muzzle' | 'orbitSlot' | 'playerCenter';

export interface WeaponVisualConfig {
  /** Radians added to the rest (idle) angle. */
  orbitAngleOffset?: number;
  /** Pivot socket as PNG fractions. Default 0.35/0.5 = the renderer's anchor. */
  pivotFx?: number;
  pivotFy?: number;
  /** Muzzle socket as PNG fractions of the weapon sprite. Absent = legacy
   *  barrelLength/barrelOffsetY path (hand-calibrated world units). */
  muzzleFx?: number;
  muzzleFy?: number;
  /** Aim lerp rate (per-second factor). 18 = the historical hardcode.
   *  SMG-class ~24 (twitchy), heavies ~9 (weighty). */
  aimTurnSpeed?: number;
  /** Where the weapon points with no target. 'outward' = radial from player
   *  (historical look), 'tangent' = along the orbit, 'fixed' = restAngle. */
  restAngleMode?: RestAngleMode;
  restAngle?: number;
  /** Idle bob (world units + rad/sec). 0 = off. Only bobs with no target. */
  idleBobAmount?: number;
  idleBobSpeed?: number;
  /** Recoil kick along -aim on fire (world units / seconds). 0 = off. */
  recoilDistance?: number;
  recoilDuration?: number;
  /** 'none' suppresses the generic 3-ring flash (weapon brings its own VFX). */
  muzzleFlashType?: MuzzleFlashType;
  /** Schema for Beta B — consumed by weaponVfx casing ejection. */
  casingEjectType?: 'none' | 'brass' | 'shell';
  projectileSpawnMode?: ProjectileSpawnMode;
  /** Extra sockets (PNG fractions) for non-gun weapons. */
  sockets?: {
    tip?: [number, number];        // melee contact
    throw?: [number, number];      // thrown-object release
    chainStart?: [number, number]; // chain whip root
    beamStart?: [number, number];  // beam origin
  };
  // orbitRadius intentionally ABSENT in Beta A — changing it is bot-visible
  // (range culling reads slot positions) and needs its own beta.
}

const DEFAULTS: Required<Pick<WeaponVisualConfig,
  'orbitAngleOffset' | 'pivotFx' | 'pivotFy' | 'aimTurnSpeed' | 'restAngleMode' |
  'restAngle' | 'idleBobAmount' | 'idleBobSpeed' | 'recoilDistance' |
  'recoilDuration' | 'muzzleFlashType' | 'casingEjectType' | 'projectileSpawnMode'
>> = {
  orbitAngleOffset: 0,
  pivotFx: 0.35,
  pivotFy: 0.5,
  aimTurnSpeed: 18,
  restAngleMode: 'outward',
  restAngle: 0,
  idleBobAmount: 0,
  idleBobSpeed: 0,
  recoilDistance: 0,
  recoilDuration: 0.14,
  muzzleFlashType: 'generic',
  casingEjectType: 'none',
  projectileSpawnMode: 'muzzle',
};

export type ResolvedWeaponVisual = typeof DEFAULTS & {
  muzzleFx?: number; muzzleFy?: number;
  sockets?: WeaponVisualConfig['sockets'];
};

/**
 * WEAPON_VISUALS — muzzle sockets auto-measured by
 * client/scripts/gen-weapon-sockets.mjs (rightmost-opaque-column heuristic,
 * the same recipe that built REVOLVER_MOD_MUZZLE), then hand-verified with
 * the ?dev=1 socket overlay. Feel numbers (aimTurnSpeed/recoil/bob) are
 * per-class: light=twitchy, heavy=weighty. FEEL ONLY — no balance impact.
 */
export const WEAPON_VISUALS: Record<string, WeaponVisualConfig> = {
  // ── GUNS — muzzle sockets AUTO-MEASURED by scripts/gen-weapon-sockets.py
  // (outermost dense alpha column; bore = centroid of outer 6 dense columns),
  // verified on the offline dot contact sheet. 3 hand corrections: revolver
  // uses the calibrated REVOLVER_MOD_MUZZLE base bore; ex_girlfriend was
  // dotted on the hanging heart charm (re-measured top-62% band); crossbow
  // bore nudged to the bolt rail. Feel classes: light=twitchy, heavy=weighty.
  // FEEL ONLY — zero balance impact.

  // Heavies — slow deliberate tracking, big kick
  peanut_rifle:      { muzzleFx: 0.9415, muzzleFy: 0.4675, aimTurnSpeed: 9,  recoilDistance: 12, recoilDuration: 0.20, idleBobAmount: 2.5, idleBobSpeed: 2.4 }, // .50 Cal
  wpn_rpg:           { muzzleFx: 0.9842, muzzleFy: 0.4361, aimTurnSpeed: 9,  recoilDistance: 13, recoilDuration: 0.22, idleBobAmount: 2.5, idleBobSpeed: 2.2 },
  wpn_railgun:       { muzzleFx: 0.9676, muzzleFy: 0.4260, aimTurnSpeed: 10, recoilDistance: 12, recoilDuration: 0.20, idleBobAmount: 2.0, idleBobSpeed: 2.2 },
  wpn_shitty_cannon: { muzzleFx: 0.9866, muzzleFy: 0.4061, aimTurnSpeed: 10, recoilDistance: 11, recoilDuration: 0.20, idleBobAmount: 3.0, idleBobSpeed: 2.0 },
  wpn_ugly_kittens:  { muzzleFx: 0.9880, muzzleFy: 0.4805, aimTurnSpeed: 10, recoilDistance: 11, recoilDuration: 0.20, idleBobAmount: 3.0, idleBobSpeed: 2.0 }, // Cat Cannon
  wpn_poop_sling:    { muzzleFx: 0.9802, muzzleFy: 0.3817, aimTurnSpeed: 11, recoilDistance: 10, recoilDuration: 0.18, idleBobAmount: 2.5, idleBobSpeed: 2.2 }, // The Thumper
  blunderbuss:       { muzzleFx: 0.9826, muzzleFy: 0.3431, aimTurnSpeed: 13, recoilDistance: 14, recoilDuration: 0.18, idleBobAmount: 2.5, idleBobSpeed: 2.4 }, // Sawed-Off

  // Mediums
  revolver:          { muzzleFx: 0.9727, muzzleFy: 0.4441, aimTurnSpeed: 16, recoilDistance: 7,  recoilDuration: 0.14, idleBobAmount: 2.5, idleBobSpeed: 2.6 },
  rpk_machine_gun:   { muzzleFx: 0.9842, muzzleFy: 0.3770, aimTurnSpeed: 15, recoilDistance: 5,  recoilDuration: 0.10, idleBobAmount: 2.5, idleBobSpeed: 2.4 },
  wpn_ultra_beam:    { muzzleFx: 0.9763, muzzleFy: 0.4174, aimTurnSpeed: 12, recoilDistance: 9,  recoilDuration: 0.18, idleBobAmount: 2.0, idleBobSpeed: 2.4 },
  wpn_crossbow:      { muzzleFx: 0.9660, muzzleFy: 0.4030, aimTurnSpeed: 14, recoilDistance: 6,  recoilDuration: 0.14, idleBobAmount: 2.0, idleBobSpeed: 2.4 },
  wpn_squirter:      { muzzleFx: 0.9141, muzzleFy: 0.4787, aimTurnSpeed: 17, recoilDistance: 4,  recoilDuration: 0.12, idleBobAmount: 3.0, idleBobSpeed: 2.8 },
  wpn_hurray:        { muzzleFx: 0.8750, muzzleFy: 0.4236, aimTurnSpeed: 15, recoilDistance: 8,  recoilDuration: 0.16, idleBobAmount: 3.0, idleBobSpeed: 2.6 }, // Bad Breakfast
  fireball_wand:     { muzzleFx: 0.9122, muzzleFy: 0.4185, aimTurnSpeed: 15, recoilDistance: 2,  recoilDuration: 0.10, idleBobAmount: 2.0, idleBobSpeed: 2.6, muzzleFlashType: 'none' }, // Firestarter hose — flame jet replaces the 3-ring flash (22.70)

  // Lights — twitchy tracking, quick small kicks
  wpn_smg:           { muzzleFx: 0.9478, muzzleFy: 0.3148, aimTurnSpeed: 26, recoilDistance: 4,  recoilDuration: 0.08, idleBobAmount: 2.0, idleBobSpeed: 3.2 },
  wpn_frozen_darts:  { muzzleFx: 0.8703, muzzleFy: 0.3815, aimTurnSpeed: 22, recoilDistance: 4,  recoilDuration: 0.10, idleBobAmount: 2.0, idleBobSpeed: 3.0 },
  wpn_ex_girlfriend: { muzzleFx: 0.8600, muzzleFy: 0.3261, aimTurnSpeed: 24, recoilDistance: 5,  recoilDuration: 0.10, idleBobAmount: 2.5, idleBobSpeed: 3.2 },

  // Thrown props — no gun recoil, a bit of alive bob while held
  viking_axe:          { aimTurnSpeed: 16, idleBobAmount: 3.0, idleBobSpeed: 2.2 },
  voogoo_rager_toss:   { aimTurnSpeed: 16, idleBobAmount: 3.0, idleBobSpeed: 2.4 }, // Beer Grenade
  tactical_basketball: { aimTurnSpeed: 16, idleBobAmount: 3.5, idleBobSpeed: 2.6 }, // Dodge This
  wpn_the_terminator:  { aimTurnSpeed: 16, idleBobAmount: 3.0, idleBobSpeed: 2.2 }, // Toilet Boomerang
  wpn_banana_bombs:    { aimTurnSpeed: 16, idleBobAmount: 3.0, idleBobSpeed: 2.4 },
  // arc (spatula_slash/dracs_fang/chain_whip), aura (chad_aura) and orbital
  // (karen_drones) weapons intentionally ABSENT — their attack paths don't
  // run through the orbit-gun pipeline; they keep legacy behavior in Beta A.
};

const _resolveCache = new Map<string, ResolvedWeaponVisual>();

export function resolveWeaponVisual(weaponId: string): ResolvedWeaponVisual {
  let r = _resolveCache.get(weaponId);
  if (!r) {
    const cfg = WEAPON_VISUALS[weaponId];
    r = { ...DEFAULTS, ...(cfg ?? {}) } as ResolvedWeaponVisual;
    _resolveCache.set(weaponId, r);
  }
  return r;
}

/** Test/dev hook: clear the resolve cache after mutating WEAPON_VISUALS. */
export function __clearWeaponVisualCache(): void {
  _resolveCache.clear();
}
